import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import U2000Panel from '../../u2000Core/components/layout/U2000Panel';
import { NS } from '../U2106';
import { useU2106AiUsageHandlers } from '../hooks/useU2106AiUsageHandlers';
import type { U2106AiUsageProvider, U2106QuotaAxis } from '../U2106Service';
import {
  u2106FormatAiUsagePeriodMonth,
  u2106FormatTokenCount,
  u2106FormatUsagePercent,
  u2106QuotaPercentTextClass,
  u2106ResolveQuotaBarTone,
} from '../tools/U2106AiUsageFormatters';
import U2106QuotaBar from './U2106QuotaBar';

function QuotaAxisBlock({
  title,
  axis,
  forceBlocked,
  unlimitedLabel,
  locale,
}: {
  title: string;
  axis: U2106QuotaAxis;
  forceBlocked: boolean;
  unlimitedLabel: string;
  locale: string;
}) {
  const tone = u2106ResolveQuotaBarTone(axis.usagePercent, axis.quotaMonthlyTokens, forceBlocked);
  const percentLabel = u2106FormatUsagePercent(axis.usagePercent, locale);
  const usageLabel = u2106FormatTokenCount(axis.usageTokens, locale);
  const quotaLabel =
    axis.quotaMonthlyTokens == null
      ? unlimitedLabel
      : u2106FormatTokenCount(axis.quotaMonthlyTokens, locale);

  return (
    <div className="rounded-lg border border-gray-200 px-3 py-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-gray-800">{title}</p>
          <p className="mt-0.5 text-xs text-gray-500">
            {usageLabel}
            {axis.quotaMonthlyTokens != null ? ` / ${quotaLabel}` : ` · ${quotaLabel}`}
          </p>
        </div>
        {percentLabel != null ? (
          <span className={`shrink-0 text-sm tabular-nums ${u2106QuotaPercentTextClass(tone)}`}>
            {percentLabel} %
          </span>
        ) : (
          <span className="shrink-0 text-sm text-gray-500">{unlimitedLabel}</span>
        )}
      </div>
      <U2106QuotaBar usagePercent={axis.usagePercent} tone={tone} />
    </div>
  );
}

function ProviderSection({
  entry,
  locale,
}: {
  entry: U2106AiUsageProvider;
  locale: string;
}) {
  const { t } = useTranslation(NS);
  const blockMessage =
    entry.blocked && entry.blockReason === 'user'
      ? t('myAiUsage.blockedUser', { ns: NS })
      : entry.blocked && entry.blockReason === 'group'
        ? t('myAiUsage.blockedGroup', { ns: NS })
        : null;

  return (
    <U2000Panel title={entry.provider.name} titleAlign="left" className="w-full">
      <div className="mt-3 space-y-3 px-2">
        {blockMessage ? (
          <div
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            role="alert"
          >
            {blockMessage}
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <QuotaAxisBlock
            title={t('myAiUsage.userQuota', { ns: NS })}
            axis={entry.user}
            forceBlocked={entry.blocked && entry.blockReason === 'user'}
            unlimitedLabel={t('myAiUsage.unlimited', { ns: NS })}
            locale={locale}
          />
          <QuotaAxisBlock
            title={t('myAiUsage.groupQuota', { ns: NS })}
            axis={entry.group}
            forceBlocked={entry.blocked && entry.blockReason === 'group'}
            unlimitedLabel={t('myAiUsage.unlimited', { ns: NS })}
            locale={locale}
          />
        </div>
      </div>
    </U2000Panel>
  );
}

/**
 * Écran lecture seule — consommation IA personnelle du mois civil org (BL-0246).
 */
const U2106AiUsageView: React.FC = () => {
  const { t, i18n } = useTranslation(NS);
  const { loading, aiUsage, loadAiUsage } = useU2106AiUsageHandlers();
  const locale = i18n.language || 'fr-CA';

  useEffect(() => {
    void loadAiUsage();
  }, [loadAiUsage]);

  if (loading && !aiUsage) {
    return (
      <div className="flex w-full justify-center p-6">
        <p className="text-sm text-gray-500">{t('myAiUsage.loading', { ns: NS })}</p>
      </div>
    );
  }

  if (!aiUsage) {
    return (
      <div className="flex w-full justify-center p-6">
        <p className="text-sm text-gray-500">{t('myAiUsage.errors.generic', { ns: NS })}</p>
      </div>
    );
  }

  const periodLabel = u2106FormatAiUsagePeriodMonth(aiUsage.period.start, locale);
  const groupLabel = aiUsage.userGroup?.name;

  return (
    <div className="flex w-full justify-center p-4">
      <div className="flex w-full max-w-4xl flex-col gap-6">
        <div className="px-1">
          <p className="text-sm text-gray-600">
            <span className="font-medium text-gray-800">{t('myAiUsage.period', { ns: NS })} :</span>{' '}
            {periodLabel}
            {groupLabel ? (
              <>
                {' · '}
                <span className="text-gray-500">
                  {t('myAiUsage.group', { ns: NS })} : {groupLabel}
                </span>
              </>
            ) : null}
          </p>
          {aiUsage.anyBlocked ? (
            <div
              className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              role="status"
            >
              {t('myAiUsage.anyBlockedBanner', { ns: NS })}
            </div>
          ) : null}
        </div>

        {aiUsage.providers.length === 0 ? (
          <p className="px-1 text-sm text-gray-500">{t('myAiUsage.empty', { ns: NS })}</p>
        ) : (
          aiUsage.providers.map((entry) => (
            <ProviderSection key={entry.provider.id} entry={entry} locale={locale} />
          ))
        )}
      </div>
    </div>
  );
};

export default U2106AiUsageView;
